import { useNavigate } from "react-router-dom";
import { Calendar, Clock, MapPin, Plus } from "lucide-react";
import { Card } from "../../components/card";
import LoginLogo from "../../assets/page/login_logo.svg";
import { formatCompactTime } from "../../lib/date-time";
import { useAuthSession } from "../auth/shared";
import { useScannerTodayEvents } from "./hooks";
import type { ScannerEvent } from "./qrScanner.api";

interface QRScannerWelcomeProps {
  basePath?: string;
}

const getFormattedDate = (): string => {
  const now = new Date();
  const day = now.getDate();
  const suffix = day > 3 && day < 21 ? "th" : (["th", "st", "nd", "rd"][day % 10] ?? "th");
  return `${now.toLocaleDateString("en-US", { weekday: "long" })}, ${now.toLocaleDateString("en-US", { month: "long" })} ${day}${suffix}`;
};

const QRScannerWelcome = ({ basePath = "/qr-scanner" }: QRScannerWelcomeProps) => {
  const navigate = useNavigate();
  const { user } = useAuthSession();
  const { events, error, isLoading, refetch } = useScannerTodayEvents();
  const isMerchantRoute = basePath === "/merchant/qr-scanner";

  const handleEventClick = (event: ScannerEvent) => {
    sessionStorage.setItem("selectedEvent", JSON.stringify(event));
    navigate(`${basePath}/event/${event.id}/camera`);
  };

  return (
    <div className="min-h-screen bg-[#F8FDFC] dark:bg-gray-900 px-6 py-8">
      <img
        src={LoginLogo}
        alt="CivicPoints"
        className="h-8 w-auto"
      />
      <div className="mt-12">
        <p className="text-gray-500 dark:text-gray-400 text-sm">{getFormattedDate()}</p>
        <h1 className="text-2xl font-bold text-[#00C49A] dark:text-emerald-400 mt-1">
          Welcome, {user ? `${user.first_name} ${user.last_name}` : "User"}
        </h1>
      </div>
      <div className="mt-10 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-[#004232] dark:text-white">Today&apos;s Events</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Select an event to start scanning
          </p>
        </div>
        {isMerchantRoute ? (
          <button
            type="button"
            onClick={() => navigate("/merchant/events/create")}
            className="flex shrink-0 items-center gap-2 rounded-lg bg-[#00C49A] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#00A982] dark:bg-emerald-500 dark:hover:bg-emerald-600"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Create Event
          </button>
        ) : null}
      </div>
      <div className="mt-4 space-y-3">
        {isLoading ? (
          [1, 2, 3].map((index) => (
            <Card
              key={index}
              className="p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border-0 animate-pulse"
            >
              <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
            </Card>
          ))
        ) : error ? (
          <div className="text-center py-12">
            <Calendar
              size={48}
              className="mx-auto text-gray-300 dark:text-gray-600 mb-4"
            />
            <p className="text-gray-500 dark:text-gray-400">{error}</p>
            <button
              onClick={() => void refetch()}
              className="mt-4 text-[#00C49A] dark:text-emerald-400 font-medium"
            >
              Try again
            </button>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <Calendar
              size={48}
              className="mx-auto text-gray-300 dark:text-gray-600 mb-4"
            />
            <p className="text-gray-500 dark:text-gray-400">No events scheduled for today</p>
          </div>
        ) : (
          events.map((event) => (
            <Card
              key={event.id}
              className="p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border-0 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleEventClick(event)}
            >
              <h3 className="font-semibold text-gray-900 dark:text-white text-base">
                {event.title}
              </h3>
              <div className="mt-3 flex items-center">
                <Clock
                  size={16}
                  className="text-gray-400 dark:text-gray-500"
                />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
                  {formatCompactTime(event.start_time)} - {formatCompactTime(event.end_time)}
                </span>
              </div>
              {event.location && (
                <div className="mt-2 flex items-center">
                  <MapPin
                    size={16}
                    className="text-gray-400 dark:text-gray-500"
                  />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
                    {event.location}
                  </span>
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default QRScannerWelcome;
